import React, { useState, useEffect } from 'react';
import {
    Paper,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Button,
    CircularProgress,
    Box,
    Divider,
    Menu,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import GetAppIcon from '@material-ui/icons/GetApp'; // Import for Export Icon
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { differenceInCalendarDays, addDays } from 'date-fns';
import {
    BusinessTotal,
    BusinessYearlyComparisonResponse,
} from '../../models';
import { getBusinessYearlyComparison } from '../../api';
import DateShortcutDropdown, { DEFAULT_SHORTCUT_OPTIONS } from '../../components/DateShortcutDropdown'; // Import the new component
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { connect } from 'react-redux';

// Register required Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    title: {
        marginBottom: theme.spacing(3),
    },
    formControl: {
        marginBottom: theme.spacing(2),
        minWidth: 120,
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    chartContainer: {
        marginTop: theme.spacing(4),
        height: '65vh', // Responsive height
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    summaryItem: {
        marginBottom: theme.spacing(1),
    },
    summaryLabel: {
        fontWeight: 'bold',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 400,
    },
    comparisonButtonRow: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    comparisonButton: {
        marginRight: theme.spacing(1),
        // marginBottom: theme.spacing(1), // Removed for better vertical alignment
    },
    filterButtonContainer: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between', // Changed from flex-start
        alignItems: 'center', // Added for vertical alignment
    },
    legendContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap', // Keep columns in a single line, allow horizontal scroll if needed
        marginTop: theme.spacing(1),
        paddingBottom: theme.spacing(1), // Add some padding if it becomes scrollable
        overflowX: 'auto', // Allow horizontal scrolling for columns
    },
    legendColumn: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: theme.spacing(3), // Space between columns
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    legendColorBox: {
        width: 24, // Increased width
        height: 12,
        marginRight: theme.spacing(1),
        border: '1px solid #ccc',
    },
    legendLabel: {
        fontSize: '0.75rem', // Decreased font size
    },
    dateDisplayContainer: {
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
}));

// Date helper functions
const getStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const getEndOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999); // End of day

const getStartOfThisMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
};
const getEndOfThisMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};
const getStartOfLastMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1);
};
const getEndOfLastMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59, 999);
};
const getStartOfThisQuarter = (): Date => {
    const date = new Date();
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
};
const getEndOfThisQuarter = (): Date => {
    const date = new Date();
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999);
};
const getStartOfLastQuarter = (): Date => {
    const date = new Date();
    const currentQuarter = Math.floor(date.getMonth() / 3);
    const firstMonthOfCurrentQuarter = currentQuarter * 3;
    return new Date(date.getFullYear(), firstMonthOfCurrentQuarter - 3, 1);
};
const getEndOfLastQuarter = (): Date => {
    const date = new Date();
    const currentQuarter = Math.floor(date.getMonth() / 3);
    const firstMonthOfCurrentQuarter = currentQuarter * 3;
    return new Date(date.getFullYear(), firstMonthOfCurrentQuarter, 0, 23, 59, 59, 999);
};
const getStartOfThisYear = (): Date => new Date(new Date().getFullYear(), 0, 1);
const getEndOfThisYear = (): Date => new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999);
const getStartOfLastYear = (): Date => new Date(new Date().getFullYear() - 1, 0, 1);
const getEndOfLastYear = (): Date => new Date(new Date().getFullYear() - 1, 11, 31, 23, 59, 59, 999);

const getLastYearSameDay = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), ref.getDate());
const getLastYearSameMonthStart = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), 1);
const getLastYearSameMonthEnd = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth() + 1, 0, 23, 59, 59, 999);
const getLastYearSameQuarterStart = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3, 1);
};
const getLastYearSameQuarterEnd = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3 + 3, 0, 23, 59, 59, 999);
};

const MAYURI_BUSINESSES = [6, 34, 69, 172, 175];
const CAN_VIEW_BUSINESS_REVENUE_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS = 'view_business_revenue_report_for_all_related_business_locations';

const BusinessYearlyComparison: React.FC = (props: any) => {
    const classes = useStyles();

    const getStartOfMonth = (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    };

    const getEndOfMonth = (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };

    const today = new Date();
    const firstDayCurrentMonth = getStartOfMonth(today);
    let initialThisPeriodEnd = getEndOfMonth(today);
    if (initialThisPeriodEnd > getEndOfDay(today)) {
        initialThisPeriodEnd = getEndOfDay(today);
    }

    const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const firstDayPrevMonth = getStartOfMonth(prevMonthDate);
    const lastDayPrevMonth = getEndOfMonth(prevMonthDate);

    const [thisPeriodStart, setThisPeriodStart] = useState<Date | null>(firstDayCurrentMonth);
    const [thisPeriodEnd, setThisPeriodEnd] = useState<Date | null>(initialThisPeriodEnd);
    const [prevPeriodStart, setPrevPeriodStart] = useState<Date | null>(firstDayPrevMonth);
    const [actualPrevPeriodEnd, setActualPrevPeriodEnd] = useState<Date | null>(lastDayPrevMonth);
    const [fullPrevPeriodEndFromShortcut, setFullPrevPeriodEndFromShortcut] = useState<Date | null>(lastDayPrevMonth);

    // State for DateShortcutDropdown selected keys
    const [currentShortcut, setCurrentShortcut] = useState<string>('this_month');
    const [previousShortcut, setPreviousShortcut] = useState<string>('last_month');
    const [limitPeriodRange, setLimitPeriodRange] = useState<boolean>(false);
    const [showLimitPeriodRangeCheckbox, setShowLimitPeriodRangeCheckbox] = useState<boolean>(true);

    const [comparisonAnchorEl, setComparisonAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedComparisonLabel, setSelectedComparisonLabel] = useState<string>('Comparison Shortcuts');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false); // New state for filter visibility
    const [businessIds, setBusinessIds] = useState<number[]>([]); // Default business IDs
    const [showAllMayuriLocations, setShowAllMayuriLocations] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<BusinessYearlyComparisonResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showLegend, setShowLegend] = useState<boolean>(false);
    const chartRef = React.useRef<ChartJS<'bar', (number | null)[], string> | null>(null);
    const [exportingCsv, setExportingCsv] = useState<boolean>(false);

    const applyComparisonShortcut = async (type: string) => {
        let currentStart: Date | null = null;
        let currentEnd: Date | null = null;
        let previousStart: Date | null = null;
        let previousEnd: Date | null = null;
        const todayDate = new Date();

        const isQuarterShortcut = type.includes('quarter');
        setShowLimitPeriodRangeCheckbox(!isQuarterShortcut);
        if (isQuarterShortcut) {
            setLimitPeriodRange(false);
        }

        switch (type) {
            case 'today_vs_yesterday':
                currentStart = getStartOfDay(todayDate);
                currentEnd = getEndOfDay(todayDate);
                const yesterday = new Date(todayDate);
                yesterday.setDate(todayDate.getDate() - 1);
                previousStart = getStartOfDay(yesterday);
                previousEnd = getEndOfDay(yesterday);
                setCurrentShortcut('today');
                setPreviousShortcut('yesterday');
                break;
            case 'today_vs_last_year_today':
                currentStart = getStartOfDay(todayDate);
                currentEnd = getEndOfDay(todayDate);
                const lastYearToday = getLastYearSameDay(todayDate);
                previousStart = getStartOfDay(lastYearToday);
                previousEnd = getEndOfDay(lastYearToday);
                setCurrentShortcut('today');
                setPreviousShortcut('last_year_today');
                break;
            case 'month_vs_last_month':
                currentStart = getStartOfThisMonth();
                currentEnd = getEndOfThisMonth();
                previousStart = getStartOfLastMonth();
                previousEnd = getEndOfLastMonth();
                setCurrentShortcut('this_month');
                setPreviousShortcut('last_month');
                break;
            case 'month_vs_last_year_month':
                currentStart = getStartOfThisMonth();
                currentEnd = getEndOfThisMonth();
                previousStart = getLastYearSameMonthStart(currentStart);
                previousEnd = getLastYearSameMonthEnd(currentEnd);
                setCurrentShortcut('this_month');
                setPreviousShortcut('last_year_this_month');
                break;
            case 'quarter_vs_last_quarter':
                currentStart = getStartOfThisQuarter();
                currentEnd = getEndOfThisQuarter();
                previousStart = getStartOfLastQuarter();
                previousEnd = getEndOfLastQuarter();
                setCurrentShortcut('this_quarter');
                setPreviousShortcut('last_quarter');
                break;
            case 'quarter_vs_last_year_quarter':
                currentStart = getStartOfThisQuarter();
                currentEnd = getEndOfThisQuarter();
                previousStart = getLastYearSameQuarterStart(currentStart);
                previousEnd = getLastYearSameQuarterEnd(currentEnd);
                setCurrentShortcut('this_quarter');
                setPreviousShortcut('last_year_this_quarter');
                break;
            case 'year_vs_last_year':
                currentStart = getStartOfThisYear();
                currentEnd = getEndOfThisYear();
                previousStart = getStartOfLastYear();
                previousEnd = getEndOfLastYear();
                setCurrentShortcut('this_year');
                setPreviousShortcut('last_year');
                break;
            default:
                return;
        }

        const todayEndOfDay = getEndOfDay(todayDate);

        if (currentEnd && currentEnd > todayEndOfDay) {
            currentEnd = todayEndOfDay;
        }
        if (previousEnd && previousEnd > todayEndOfDay) {
            previousEnd = todayEndOfDay;
        }

        setThisPeriodStart(currentStart);
        setThisPeriodEnd(currentEnd);
        setPrevPeriodStart(previousStart);
        setFullPrevPeriodEndFromShortcut(previousEnd); // previousEnd is the full (future-capped) P2 end from shortcut

        // actualPrevPeriodEnd will be updated by the useEffect calling recalculateActualPrevPeriodEnd
        // Removed automatic fetchData call
    };

    const handleComparisonMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setComparisonAnchorEl(event.currentTarget);
    };

    const handleComparisonMenuClose = () => {
        setComparisonAnchorEl(null);
    };

    const handleComparisonSelect = (type: string, label: string) => {
        applyComparisonShortcut(type);
        setSelectedComparisonLabel(label);
        handleComparisonMenuClose();
    };

    // Function to format currency
    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(value);
    };

    const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined;
        // Format date as YYYY-MM-DD
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateForDisplay = (date: Date | null): string => {
        if (!date) return 'N/A';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Function to fetch data from API
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        if (businessIds.length === 0) {
            setError("Please select at least one business.");
            setLoading(false);
            return;
        }
        const businessIdsString = businessIds.join(",");
        if (!thisPeriodStart || !thisPeriodEnd || !prevPeriodStart || !actualPrevPeriodEnd) { // Check actualPrevPeriodEnd
            setError("Please select valid start and end dates for both periods.");
            setLoading(false);
            return;
        }
        try {
            const response = await getBusinessYearlyComparison(
                formatDateForAPI(thisPeriodStart),
                formatDateForAPI(thisPeriodEnd),
                formatDateForAPI(prevPeriodStart),
                formatDateForAPI(actualPrevPeriodEnd), // Use actualPrevPeriodEnd
                businessIdsString
            );
            if (response) {
                const jsonData = await response.json();
                setData(jsonData);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleExportCsv = async () => {
        if (businessIds.length === 0) {
            setError("Please select at least one business to export.");
            return;
        }
        if (!thisPeriodStart || !thisPeriodEnd || !prevPeriodStart || !actualPrevPeriodEnd) { // Check actualPrevPeriodEnd
            setError("Please ensure all date periods are selected to export.");
            return;
        }

        setExportingCsv(true);
        setError(null);
        const businessIdsString = businessIds.join(',');

        try {
            // Assuming getBusinessYearlyComparison is updated to accept a 6th boolean param for export_csv
            const response = await getBusinessYearlyComparison(
                formatDateForAPI(thisPeriodStart),
                formatDateForAPI(thisPeriodEnd),
                formatDateForAPI(prevPeriodStart),
                formatDateForAPI(actualPrevPeriodEnd), // Use actualPrevPeriodEnd
                businessIdsString,
                true // Request CSV export
            );

            if (response && response.ok) {
                const blob = await response.blob();
                const disposition = response.headers.get('content-disposition');
                let filename = `business_yearly_comparison_export_${new Date().toISOString().split('T')[0]}.csv`; // Default filename
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                    }
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else {
                const errorText = response ? await response.text() : 'No response';
                console.error('Failed to export CSV:', errorText);
                setError(`Failed to export CSV. Server said: ${response?.statusText || errorText}`);
            }
        } catch (err: any) {
            console.error('Error exporting CSV:', err);
            setError(`An error occurred while exporting CSV: ${err.message || 'Unknown error'}`);
        } finally {
            setExportingCsv(false);
        }
    };

    // Removed useEffect that was auto-fetching data on date/businessId change.
    // Fetching is now manual via the "Generate Report" button.

    const recalculateActualPrevPeriodEnd = () => {
        // In this file, P1 is thisPeriodStart/End, P2 is prevPeriodStart/actualPrevPeriodEnd
        // currentShortcut refers to P1, previousShortcut to P2.
        const baseP2End = previousShortcut === 'custom' ? actualPrevPeriodEnd : fullPrevPeriodEndFromShortcut;

        if (limitPeriodRange && thisPeriodStart && thisPeriodEnd && prevPeriodStart) {
            const p1DurationDays = differenceInCalendarDays(thisPeriodEnd, thisPeriodStart) + 1;
            if (p1DurationDays > 0) {
                let limitedP2End = addDays(prevPeriodStart, p1DurationDays - 1);
                setActualPrevPeriodEnd(getEndOfDay(limitedP2End));
            } else {
                setActualPrevPeriodEnd(prevPeriodStart ? getEndOfDay(new Date(prevPeriodStart)) : null);
            }
        } else {
            setActualPrevPeriodEnd(baseP2End);
        }
    };

    useEffect(() => {
        recalculateActualPrevPeriodEnd();
    }, [limitPeriodRange, thisPeriodStart, thisPeriodEnd, prevPeriodStart, fullPrevPeriodEndFromShortcut, previousShortcut]);

    useEffect(() => {
        const p1HasQuarter = currentShortcut.includes('quarter');
        const p2HasQuarter = previousShortcut.includes('quarter');

        if (p1HasQuarter || p2HasQuarter) {
            setShowLimitPeriodRangeCheckbox(false);
            setLimitPeriodRange(false);
        } else {
            setShowLimitPeriodRangeCheckbox(true);
        }
    }, [currentShortcut, previousShortcut, setShowLimitPeriodRangeCheckbox, setLimitPeriodRange]);

    useEffect(() => {
        const sb = props.userData.selectedBusiness;

        if (!sb || !sb.business_id) { // Ensure sb and sb.business_id exist
            setBusinessIds([]);
            return;
        }

        const selectedBusinessId = parseInt(sb.business_id); // Use parseInt consistently
        const userPermissions = props.userData.userData?.userData?.user_permissions	?.map((p: any) => p.codename) || [];
        const isSuperuser = props.userData.userData?.userData?.is_superuser === true;
        const hasSpecificPermission = userPermissions.includes(CAN_VIEW_BUSINESS_REVENUE_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS);

        const canActivateAllMayuriMode = isSuperuser || hasSpecificPermission;

        if (showAllMayuriLocations && MAYURI_BUSINESSES.includes(selectedBusinessId) && canActivateAllMayuriMode) {
            setBusinessIds(MAYURI_BUSINESSES);
        } else {
            // Ensure selectedBusinessId is a valid number before putting in array
            setBusinessIds(isNaN(selectedBusinessId) ? [] : [selectedBusinessId]);
        }
    }, [props.userData.selectedBusiness, props.userData.userData, showAllMayuriLocations]);

    // Prepare chart data
    const chartData = React.useMemo(() => {
        if (!data) {
            return {
                labels: [],
                datasets: [
                    {
                        label: `Period 1 Gross Total`,
                        data: [],
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    },
                    {
                        label: `Period 2 Gross Total`,
                        data: [],
                        backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    },
                ],
            };
        }

        const currentPeriodMap = new Map<string, number>();
        data.current_period_totals.forEach(item => {
            currentPeriodMap.set(item.business_id, item.gross_total);
        });

        const previousPeriodMap = new Map<string, number>();
        data.previous_period_totals.forEach(item => {
            previousPeriodMap.set(item.business_id, item.gross_total);
        });

        // Consolidate all business info (id and name)
        const allBusinesses: { id: string, name: string }[] = [];
        const seenBusinessIds = new Set<string>();

        data.current_period_totals.forEach(item => {
            if (!seenBusinessIds.has(item.business_id)) {
                allBusinesses.push({ id: item.business_id, name: item.business_name });
                seenBusinessIds.add(item.business_id);
            }
        });
        data.previous_period_totals.forEach(item => {
            if (!seenBusinessIds.has(item.business_id)) {
                allBusinesses.push({ id: item.business_id, name: item.business_name });
                seenBusinessIds.add(item.business_id);
            }
        });

        // Sort businesses by name for consistent chart labeling
        allBusinesses.sort((a, b) => a.name.localeCompare(b.name));

        const labels = allBusinesses.map(b => b.name);
        const currentPeriodData = allBusinesses.map(b => currentPeriodMap.get(b.id) || 0);
        const previousPeriodData = allBusinesses.map(b => previousPeriodMap.get(b.id) || 0);

        return {
            labels: labels,
            datasets: [
                {
                    label: `Period 1 Gross Total`,
                    data: currentPeriodData,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                },
                {
                    label: `Period 2 Gross Total`,
                    data: previousPeriodData,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                },
            ],
        };
    }, [data]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const, // Change to horizontal bar chart
        scales: {
            x: { // X-axis is now the value axis
                beginAtZero: true,
                ticks: {
                    callback: (value: number) => formatCurrency(value),
                },
            },
            y: { // Y-axis is now the category axis
                // No specific config needed for y-axis labels for now
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = context.raw;
                        return `${context.dataset.label}: ${formatCurrency(value)}`;
                    },
                },
            },
            legend: {
                position: 'top' as const,
                display: false, // Disable default Chart.js legend
            },
            title: {
                display: false, // Changed from true
                text: `Business Comparison: Period 2 vs Period 1`,
            },
            datalabels: {
                anchor: 'end' as const,
                align: 'right' as const, // Adjust alignment for horizontal bars
                formatter: (value: number, context: any) => {
                    return formatCurrency(value);
                },
                font: {
                    weight: 'bold' as const, // Explicitly cast to the literal type
                },
                color: '#444' // Optional: Change label color for better visibility
            } as const
        },
    };

    // Calculate total and growth
    const calculateTotals = () => {
        if (!data) return null;

        const currentPeriodTotal = data.current_period_totals.reduce(
            (sum, item) => sum + item.gross_total,
            0
        );
        const previousPeriodTotal = data.previous_period_totals.reduce(
            (sum, item) => sum + item.gross_total,
            0
        );
        const growth = currentPeriodTotal - previousPeriodTotal;
        const growthPercentage = previousPeriodTotal !== 0
            ? (growth / previousPeriodTotal) * 100
            : 0;

        return {
            currentPeriodTotal,
            previousPeriodTotal,
            growth,
            growthPercentage,
        };
    };

    const totals = calculateTotals();

    const handleGenerateReportClick = () => {
        fetchData();
    };

    // Custom Legend Component
    const CustomLegend: React.FC<{ chart: ChartJS<'bar', (number | null)[], string> | null, datasets: any[], visible: boolean, classes: any }> = ({ chart, datasets, visible, classes }) => {
        if (!visible || !datasets || datasets.length === 0) {
            return null;
        }

        const legendItemsWithIndex = datasets.map((dataset: any, index: number) => ({
            label: dataset.label,
            color: dataset.backgroundColor,
            datasetIndex: index,
        })).sort((a, b) => { // Simple sort for Period 1 then Period 2
            if (a.label.includes('Period 1') && b.label.includes('Period 2')) return -1;
            if (a.label.includes('Period 2') && b.label.includes('Period 1')) return 1;
            return a.label.localeCompare(b.label);
        });

        const itemsPerColumn = 5;
        const columns: any[][] = [];
        for (let i = 0; i < legendItemsWithIndex.length; i += itemsPerColumn) {
            columns.push(legendItemsWithIndex.slice(i, i + itemsPerColumn));
        }

        const handleLegendItemClick = (datasetIndex: number) => {
            if (chart) {
                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                chart.update();
            }
        };

        return (
            <Box className={classes.legendContainer}>
                {columns.map((columnItems, colIndex) => (
                    <Box key={colIndex} className={classes.legendColumn}>
                        {columnItems.map((item) => {
                            const isHidden = chart ? !chart.isDatasetVisible(item.datasetIndex) : false;
                            return (
                                <Box
                                    key={`${item.label}-${item.datasetIndex}`}
                                    className={classes.legendItem}
                                    onClick={() => handleLegendItemClick(item.datasetIndex)}
                                    style={{ cursor: 'pointer' /* Opacity handled by Typography now */ }}
                                >
                                    <Box className={classes.legendColorBox} style={{ backgroundColor: item.color }} />
                                    <Typography
                                        className={classes.legendLabel}
                                        style={{ textDecoration: isHidden ? 'line-through' : 'none', opacity: isHidden ? 0.5 : 1 }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        );
    };

    const canSeeToggle = () => {
        const isBusinessForMultiple = props.userData.selectedBusiness && MAYURI_BUSINESSES.includes(parseInt(props.userData.selectedBusiness.business_id))
        // console.log("userdata", props.userData.userData)
        
        const isSuperUser = props.userData.userData?.userData?.is_superuser === true
        const hasSpecificPermission = props.userData.userData?.userData?.user_permissions?.find((p: any) => p.codename === CAN_VIEW_BUSINESS_REVENUE_REPORT_FOR_ALL_RELATED_BUSINESS_LOCATIONS)

        console.log(isBusinessForMultiple, isSuperUser, hasSpecificPermission)
        return isBusinessForMultiple && (isSuperUser || hasSpecificPermission)
    }

    return (
        <Paper className={classes.root}>
            <Typography variant="h5" className={classes.title}>
            Business Revenue Comparison
            </Typography>

            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container spacing={2} alignItems="center" className={classes.filterButtonContainer}>
                    {/* Left-aligned controls */}
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Button
                                    aria-controls="comparison-menu"
                                    aria-haspopup="true"
                                    variant="outlined"
                                    onClick={handleComparisonMenuOpen}
                                    className={classes.comparisonButton}
                                    endIcon={<ArrowDropDownIcon />}
                                >
                                    {selectedComparisonLabel}
                                </Button>
                                <Menu
                                    id="comparison-menu"
                                    anchorEl={comparisonAnchorEl}
                                    keepMounted
                                    open={Boolean(comparisonAnchorEl)}
                                    onClose={handleComparisonMenuClose}
                                >
                                    <MenuItem onClick={() => handleComparisonSelect('today_vs_yesterday', 'Today vs Yesterday')}>Today vs Yesterday</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('today_vs_last_year_today', 'Today vs Last Year Today')}>Today vs Last Year Today</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('month_vs_last_month', 'This Month vs Last Month')}>This Month vs Last Month</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('month_vs_last_year_month', 'This Month vs Last Year This Month')}>This Month vs Last Year This Month</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('quarter_vs_last_quarter', 'This Quarter vs Last Quarter')}>This Quarter vs Last Quarter</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('quarter_vs_last_year_quarter', 'This Quarter vs Last Year This Quarter')}>This Quarter vs Last Year This Quarter</MenuItem>
                                    <MenuItem onClick={() => handleComparisonSelect('year_vs_last_year', 'This Year vs Last Year')}>This Year vs Last Year</MenuItem>
                                </Menu>
                            </Grid>
                            {showLimitPeriodRangeCheckbox && (
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={limitPeriodRange}
                                                onChange={(e) => setLimitPeriodRange(e.target.checked)}
                                                name="limitPeriodRange"
                                                color="primary"
                                            />
                                        }
                                        label="Limit Period Range"
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                    startIcon={<FilterListIcon />}
                                >
                                    {showAdvancedFilters ? 'Hide Filters' : 'Show Filters'}
                                </Button>
                            </Grid>
                            { canSeeToggle() && (
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showAllMayuriLocations}
                                                onChange={(e) => setShowAllMayuriLocations(e.target.checked)}
                                                name="showAllMayuriLocations"
                                                color="primary"
                                            />
                                        }
                                        label="Across All Businesses"
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowLegend(!showLegend)}
                                    startIcon={showLegend ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                >
                                    {showLegend ? 'Hide Legend' : 'Show Legend'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right-aligned action buttons */}
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={handleExportCsv}
                                    startIcon={<GetAppIcon />}
                                    disabled={exportingCsv || loading || !data} // Disable if exporting, loading data, or no data
                                >
                                    {exportingCsv ? <CircularProgress size={20} /> : 'Export CSV'}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleGenerateReportClick}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {showAdvancedFilters && (
                    <form> {/* Removed onSubmit={handleSubmit} */}
                        <Grid container spacing={3} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={3}>
                                <DateShortcutDropdown
                                    key={`current-${currentShortcut}`}
                                    label="Period 1 Shortcuts"
                                    setStartDate={(newDate) => setThisPeriodStart(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    setEndDate={(newDate) => setThisPeriodEnd(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    value={currentShortcut}
                                    onChange={(newShortcut) => {
                                        if (newShortcut !== currentShortcut) {
                                            setCurrentShortcut(newShortcut);
                                        }
                                    }}
                                    className={classes.formControl}
                                    shortcutOptions={DEFAULT_SHORTCUT_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DateShortcutDropdown
                                    key={`previous-${previousShortcut}`}
                                    label="Period 2 Shortcuts"
                                    setStartDate={(newDate) => setPrevPeriodStart(currentDate => {
                                        if (newDate === null && currentDate === null) return currentDate;
                                        if (newDate === null || currentDate === null) return newDate;
                                        if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                        return currentDate;
                                    })}
                                    setEndDate={(newDate) => {
                                       setFullPrevPeriodEndFromShortcut(currentFullPrevEnd => {
                                            if (newDate === null && currentFullPrevEnd === null) return currentFullPrevEnd;
                                            if (newDate === null || currentFullPrevEnd === null) return newDate;
                                            if (newDate.getTime() !== currentFullPrevEnd.getTime()) {
                                                return newDate;
                                            }
                                            return currentFullPrevEnd;
                                        });
                                    }}
                                    value={previousShortcut}
                                    onChange={(newShortcut) => {
                                        if (newShortcut !== previousShortcut) {
                                            setPreviousShortcut(newShortcut);
                                        }
                                    }}
                                    shortcutOptions={DEFAULT_SHORTCUT_OPTIONS}
                                    className={classes.formControl}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} alignItems="flex-start"> {/* New row for date pickers */}
                            <Grid item xs={12} sm={6} md={3}>
                                {currentShortcut === 'custom' && (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 1 Start"
                                                value={thisPeriodStart}
                                                onChange={(date) => setThisPeriodStart(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 1 End"
                                                value={thisPeriodEnd}
                                                onChange={(date) => setThisPeriodEnd(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                {previousShortcut === 'custom' && (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 2 Start"
                                                value={prevPeriodStart}
                                                onChange={(date) => setPrevPeriodStart(date ? new Date(date as any) : null)}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <KeyboardDatePicker
                                                fullWidth
                                                label="Period 2 End"
                                                value={actualPrevPeriodEnd}
                                                onChange={(date) => {
                                                   const newDate = date ? getEndOfDay(new Date(date as any)) : null;
                                                   setActualPrevPeriodEnd(newDate);
                                                   setPreviousShortcut('custom');
                                                }}
                                                format="yyyy-MM-dd"
                                                className={classes.formControl}
                                                autoOk
                                                openTo="month"
                                                views={['year', 'month', 'date']}
                                                maxDate={getEndOfDay(new Date())}
                                                maxDateMessage="Date cannot be in the future"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {/* "Generate Report" button moved to the main button row */}
                    </form>
                )}
            </MuiPickersUtilsProvider>

            <Divider className={classes.divider} />

            {loading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : data ? (
                <>
                    {totals && (
                        <Grid container spacing={2} className={classes.summaryItem}>
                            <Grid item xs={12} sm={3}>
                                <Typography>
                                    <span className={classes.summaryLabel}>Period 1 Total:</span>{' '}
                                    {formatCurrency(totals.currentPeriodTotal)}
                                </Typography>
                                <Typography variant="caption" component="div">
                                    {formatDateForDisplay(thisPeriodStart)} - {formatDateForDisplay(thisPeriodEnd)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography>
                                    <span className={classes.summaryLabel}>Period 2 Total:</span>{' '}
                                    {formatCurrency(totals.previousPeriodTotal)}
                                </Typography>
                                <Typography variant="caption" component="div">
                                    {formatDateForDisplay(prevPeriodStart)} - {formatDateForDisplay(actualPrevPeriodEnd)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography>
                                    <span className={classes.summaryLabel}>Change:</span>{' '}
                                    {formatCurrency(totals.growth)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography>
                                    <span className={classes.summaryLabel}>Change Percentage:</span>{' '}
                                    {totals.growthPercentage.toFixed(2)}%
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    {/* Button moved to the filter row */}
                    {/* Date display is now part of the summary items below */}
                    <CustomLegend chart={chartRef.current} datasets={chartData.datasets} visible={showLegend} classes={classes} />
                    <Box className={classes.chartContainer}>
                        <Bar ref={chartRef} data={chartData} options={chartOptions} />
                    </Box>
                </>
            ) : (
                <Typography>No data available. Please generate the report.</Typography>
            )}
        </Paper>
    );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.userLocalData,
  };
};
export default connect(mapStateToProps, null)(BusinessYearlyComparison);
